import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PlusIcon, ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { ScrollManager } from '../utils/scrollManager';
import MarkdownRenderer from '../components/MarkdownRenderer';
// import { getScoreColors } from '../utils/colorUtils'; // Unused for now
import HypothesesList from '../components/hypotheses/HypothesesList';
import HypothesisForm from '../components/HypothesisForm';
import PermissionGate from '../components/PermissionGate';
import ReadOnlyBadge from '../components/ReadOnlyBadge';
import { Hypothesis, Problem } from '../types';

// Component for detailed hypothesis view
const HypothesisDetailView: React.FC<{
  hypothesis: Hypothesis;
  problems: Problem[];
  allHypotheses: Hypothesis[];
  onBack: () => void;
  onEdit: (hypothesis: Hypothesis) => void;
  onDelete?: (hypothesisId: string) => void;
  autoEdit?: boolean;
}> = ({ hypothesis, problems, allHypotheses, onBack, onEdit, onDelete, autoEdit = false }) => {
  const { get } = useApi();
  const { isViewer } = useAuth();
  const navigate = useNavigate();
  const [problem, setProblem] = useState<Problem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchProblem = async () => {
      try {
        setIsLoading(true);
        const problemData = await get<Problem>(`/problems/${hypothesis.problem_id}`);
        setProblem(problemData);
      } catch (err) {
        console.error('Error fetching problem:', err);
        setProblem(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProblem();
  }, [get, hypothesis.problem_id]);

  // Auto-enter edit mode when coming from report
  useEffect(() => {
    if (autoEdit && !isLoading) {
      setIsEditing(true);
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [autoEdit, isLoading]);

  const handleEditSubmit = (updatedHypothesis: Hypothesis) => {
    onEdit(updatedHypothesis);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(hypothesis.id);
      onBack(); // Navigate back to list after delete
    }
    setShowDeleteModal(false);
  };

  // Navigation helpers
  const currentIndex = allHypotheses.findIndex(h => h.id === hypothesis.id);
  const prevHypothesis = currentIndex > 0 ? allHypotheses[currentIndex - 1] : null;
  const nextHypothesis = currentIndex < allHypotheses.length - 1 ? allHypotheses[currentIndex + 1] : null;

  if (isEditing) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setIsEditing(false)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Hypothesis
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Hypothesis</h1>
        </div>
        <HypothesisForm
          hypothesis={hypothesis}
          problems={problems}
          onSubmit={handleEditSubmit}
          onCancel={() => setIsEditing(false)}
          isEditing={true}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Hypotheses
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{hypothesis.title}</h1>
            <div className="mt-2 flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                Business Problem: {isLoading ? 'Loading...' : (problem?.title || 'Unknown')}
              </span>
              <span className="text-sm font-medium text-indigo-600">
                Priority Score: {hypothesis.priority_score?.toFixed(2) || 'Not calculated'}
              </span>
              {isViewer() && <ReadOnlyBadge />}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Navigation buttons */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => prevHypothesis && navigate(`/app-hypotheses/${prevHypothesis.id}`)}
                disabled={!prevHypothesis}
                className="p-2 border border-gray-300 rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous hypothesis"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-500">
                {currentIndex + 1} of {allHypotheses.length}
              </span>
              <button
                onClick={() => nextHypothesis && navigate(`/app-hypotheses/${nextHypothesis.id}`)}
                disabled={!nextHypothesis}
                className="p-2 border border-gray-300 rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next hypothesis"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>

            <PermissionGate requireWrite>
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Edit
              </button>
              {onDelete && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              )}
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Hypothesis Statement */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Hypothesis Statement</h2>
        <p className="text-gray-700 font-medium text-lg italic bg-blue-50 p-4 rounded border-l-4 border-blue-400">
          {hypothesis.hypothesis_statement}
        </p>
      </div>

      {/* Description */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Description</h2>
        <MarkdownRenderer content={hypothesis.description} />
      </div>

      {/* AI Capability and Expected Outcome */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">AI Capability</h2>
          <MarkdownRenderer content={hypothesis.ai_capability} />
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Expected Business Outcome</h2>
          <MarkdownRenderer content={hypothesis.expected_outcome} />
        </div>
      </div>

      {/* Scoring */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Scoring</h2>
        <div className="grid grid-cols-5 gap-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{hypothesis.business_impact}/10</div>
            <div className="text-xs text-gray-500">Business Impact</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{hypothesis.technical_feasibility}/10</div>
            <div className="text-xs text-gray-500">Technical Feasibility</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{hypothesis.strategy_alignment}/10</div>
            <div className="text-xs text-gray-500">Strategic Alignment</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{hypothesis.implementation_effort}/10</div>
            <div className="text-xs text-gray-500">Implementation Effort</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600">{hypothesis.priority_score?.toFixed(2) || 'N/A'}</div>
            <div className="text-xs text-gray-500">Priority Score</div>
          </div>
        </div>
      </div>

      {/* Testing Requirements */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Testing Requirements</h2>
        <MarkdownRenderer content={hypothesis.testing_requirements} />
      </div>

      {/* Metrics to Track */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Metrics to Track</h2>
        {hypothesis.metrics_to_track && hypothesis.metrics_to_track.length > 0 ? (
          <ul className="space-y-2">
            {hypothesis.metrics_to_track.map((metric, index) => (
              <li key={index} className="flex items-start">
                <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <MarkdownRenderer content={metric} className="text-gray-700" />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic">No metrics defined for tracking this hypothesis</p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Hypothesis
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete "{hypothesis.title}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                  onClick={handleDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HypothesesPage: React.FC = () => {
  const { get, post, put, delete: deleteHypothesis } = useApi();
  const { isViewer } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Get problemId from URL search params for pre-selection
  const searchParams = new URLSearchParams(location.search);
  const preSelectedProblemId = searchParams.get('problemId');
  const shouldAutoEdit = searchParams.get('edit') === 'true';
  
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [selectedHypothesis, setSelectedHypothesis] = useState<Hypothesis | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [problemsList, setProblemsList] = useState<Problem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state hooks - must be at component top level
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('');
  const [selectedProblemFilter, setSelectedProblemFilter] = useState<string>('');


  // Determine current view based on URL
  const isCreateView = location.pathname === '/app-hypotheses/new';
  const isDetailView = !!id && !isCreateView;

  // Fetch hypotheses and problems from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [hypothesesData, problemsData] = await Promise.all([
          get<Hypothesis[]>('/hypotheses'),
          get<Problem[]>('/problems')
        ]);
        
        setHypotheses(Array.isArray(hypothesesData) ? hypothesesData : []);
        
        // Create problems lookup
        const problemsLookup: Record<string, string> = {};
        if (Array.isArray(problemsData)) {
          problemsData.forEach(p => {
            problemsLookup[p.id] = p.title;
          });
        }
        setProblems(problemsLookup);
        setProblemsList(Array.isArray(problemsData) ? problemsData : []);
        
        // If viewing a specific hypothesis, fetch its details
        if (id && id !== 'new') {
          const hypothesis = await get<Hypothesis>(`/hypotheses/${id}`);
          setSelectedHypothesis(hypothesis);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [get, id]);

  // Don't show loading spinner for create view if we have problems data
  const shouldShowLoading = isLoading && !(isCreateView && problemsList.length > 0);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleHypothesisCreated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis created, updating hypotheses list');
      setHypotheses(prev => [...prev, data.hypothesis]);
    };

    const handleHypothesisUpdated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis updated, updating hypotheses list');
      setHypotheses(prev => 
        prev.map(h => h.id === data.hypothesis.id ? data.hypothesis : h)
      );
      // Update selected hypothesis if it's the one being viewed
      if (selectedHypothesis?.id === data.hypothesis.id) {
        setSelectedHypothesis(data.hypothesis);
      }
    };

    const handleHypothesisDeleted = (data: { hypothesis_id: string }) => {
      console.log('Hypothesis deleted, updating hypotheses list');
      setHypotheses(prev => prev.filter(h => h.id !== data.hypothesis_id));
      // If viewing the deleted hypothesis, navigate back to list
      if (selectedHypothesis?.id === data.hypothesis_id) {
        navigate('/app-hypotheses');
      }
    };

    // Register event listeners
    socket.on('hypothesis_created', handleHypothesisCreated);
    socket.on('hypothesis_updated', handleHypothesisUpdated);
    socket.on('hypothesis_deleted', handleHypothesisDeleted);

    // Cleanup listeners on unmount
    return () => {
      socket.off('hypothesis_created', handleHypothesisCreated);
      socket.off('hypothesis_updated', handleHypothesisUpdated);
      socket.off('hypothesis_deleted', handleHypothesisDeleted);
    };
  }, [socket, isConnected, selectedHypothesis, navigate]);

  // Form submission handlers
  const handleCreateHypothesis = async (hypothesisData: Hypothesis) => {
    try {
      const newHypothesis = await post<Hypothesis>('/hypotheses', hypothesisData);
      setHypotheses(prev => [...prev, newHypothesis]);
      navigate('/app-hypotheses');
    } catch (error) {
      console.error('Error creating hypothesis:', error);
      setError('Failed to create hypothesis. Please try again.');
    }
  };

  const handleUpdateHypothesis = async (hypothesisData: Hypothesis) => {
    try {
      const updatedHypothesis = await put<Hypothesis>(`/hypotheses/${hypothesisData.id}`, hypothesisData);
      setHypotheses(prev => 
        prev.map(h => h.id === updatedHypothesis.id ? updatedHypothesis : h)
      );
      setSelectedHypothesis(updatedHypothesis);
      
      // Check if we need to return to report
      if (ScrollManager.hasPendingReturn()) {
        navigate('/reports');
      } else {
        navigate(`/app-hypotheses/${updatedHypothesis.id}`);
      }
    } catch (error) {
      console.error('Error updating hypothesis:', error);
      setError('Failed to update hypothesis. Please try again.');
    }
  };

  const handleDeleteHypothesis = async (hypothesisId: string) => {
    try {
      await deleteHypothesis(`/hypotheses/${hypothesisId}`);
      setHypotheses(prev => prev.filter(h => h.id !== hypothesisId));
      // Navigate back to list view
      navigate('/app-hypotheses');
    } catch (error) {
      console.error('Error deleting hypothesis:', error);
      setError('Failed to delete hypothesis. Please try again.');
    }
  };

  if (shouldShowLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading hypotheses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p className="font-medium">Error loading hypotheses</p>
        <p className="mt-1 text-sm">{error}</p>
      </div>
    );
  }

  // Create Hypothesis View
  if (isCreateView) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => navigate('/app-hypotheses')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Hypotheses
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create New AI Hypothesis</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create and manage AI hypotheses that connect AI capabilities to business problems
          </p>
        </div>
        <HypothesisForm
          problems={problemsList}
          preSelectedProblemId={preSelectedProblemId}
          onSubmit={handleCreateHypothesis}
          onCancel={() => navigate('/app-hypotheses')}
        />
      </div>
    );
  }

  // Hypothesis Detail View
  if (isDetailView && selectedHypothesis) {
    return (
      <HypothesisDetailView 
        hypothesis={selectedHypothesis} 
        problems={problemsList}
        allHypotheses={hypotheses}
        onBack={() => navigate('/app-hypotheses')}
        onEdit={handleUpdateHypothesis}
        onDelete={handleDeleteHypothesis}
        autoEdit={shouldAutoEdit}
      />
    );
  }

  // Hypotheses List View (default)
  // Filter hypotheses by selected project and/or problem
  let filteredHypotheses = hypotheses;
  if (selectedProjectFilter) {
    filteredHypotheses = filteredHypotheses.filter(h => h.project_id === selectedProjectFilter);
  }
  if (selectedProblemFilter) {
    filteredHypotheses = filteredHypotheses.filter(h => h.problem_id === selectedProblemFilter);
  }

  // Get problems for the selected project filter
  const problemsForSelectedProject = selectedProjectFilter 
    ? problemsList.filter(p => p.project_id === selectedProjectFilter)
    : problemsList;

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">AI Hypotheses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage AI hypotheses that connect AI capabilities to business problems
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isViewer() && <ReadOnlyBadge />}
          <PermissionGate requireWrite>
            <button
              onClick={() => navigate('/app-hypotheses/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              New Hypothesis
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Project and Problem Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4 mb-6 border border-gray-200">
        <div className="space-y-4">
          {/* First row: Project Filter */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="project-filter" className="text-sm font-medium text-gray-700">
                  Filter by Project:
                </label>
                <select
                  id="project-filter"
                  value={selectedProjectFilter}
                  onChange={(e) => {
                    setSelectedProjectFilter(e.target.value);
                    setSelectedProblemFilter(''); // Clear problem filter when project changes
                  }}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm min-w-48"
                >
                  <option value="">All Projects</option>
                  {Array.from(new Set(hypotheses.map(h => h.project_id).filter(Boolean))).map((projectId) => (
                    <option key={projectId} value={projectId}>
                      Project {projectId}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedProjectFilter && (
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    Project {selectedProjectFilter}
                  </span>
                  <button
                    onClick={() => navigate(`/app-projects?view=detail&id=${selectedProjectFilter}`)}
                    className="inline-flex items-center px-3 py-1.5 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                  >
                    📋 View Project Context
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Second row: Problem Filter */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="problem-filter" className="text-sm font-medium text-gray-700">
                  Filter by Problem:
                </label>
                <select
                  id="problem-filter"
                  value={selectedProblemFilter}
                  onChange={(e) => setSelectedProblemFilter(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm min-w-48"
                  disabled={!problemsForSelectedProject.length}
                >
                  <option value="">All Problems</option>
                  {problemsForSelectedProject.map((problem) => (
                    <option key={problem.id} value={problem.id}>
                      {problem.title}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedProblemFilter && (
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {problemsList.find(p => p.id === selectedProblemFilter)?.title}
                  </span>
                  <button
                    onClick={() => navigate(`/app-problems/${selectedProblemFilter}`)}
                    className="inline-flex items-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                  >
                    🎯 View Problem Details
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Third row: Summary and Clear Filters */}
          {(selectedProjectFilter || selectedProblemFilter) && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  Showing {filteredHypotheses.length} hypothes{filteredHypotheses.length !== 1 ? 'es' : 'is'}
                  {selectedProjectFilter && selectedProblemFilter ? ' for selected project and problem' :
                   selectedProjectFilter ? ' for selected project' :
                   ' for selected problem'}
                </span>
              </div>
              <button
                onClick={() => {
                  setSelectedProjectFilter('');
                  setSelectedProblemFilter('');
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
      
      <HypothesesList
        hypotheses={filteredHypotheses}
        problems={problems}
        loading={isLoading}
        emptyMessage={
          selectedProjectFilter || selectedProblemFilter
            ? "No hypotheses found for the selected filters."
            : "No hypotheses found. Create your first hypothesis to get started."
        }
        showProblemTitles={true}
        showProjectContext={!selectedProjectFilter} // Show project context when not filtering by project
        showCreateButton={true}
        createButtonText="Create New Hypothesis"
      />
    </div>
  );
};

export default HypothesesPage;